import * as React from 'react';

import CheckBox, { CheckboxProps } from '@mui/material/Checkbox';
import { styled } from '@mui/material/styles';

import { Icon } from '../icon';

const checkboxBoxStyle = {
  width: 18,
  height: 18,
  borderRadius: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

type ExtendedCheckboxProps = CheckboxProps & {
  updatedStyling?: boolean;
};

const UncheckedIcon = styled('span')<ExtendedCheckboxProps>(
  ({ theme, updatedStyling }) => ({
    ...checkboxBoxStyle,
    border: updatedStyling
      ? `1px solid ${theme.border?.checkboxUpdated}`
      : undefined,
    borderRadius: '4px',
    backgroundColor: theme.background?.white,
    zIndex: 1,
    boxShadow: !updatedStyling ? theme.colors.shadows.checkBox : undefined,
  })
);

const CheckedIcon = styled('span')<ExtendedCheckboxProps>(
  ({ theme, updatedStyling }) => ({
    ...checkboxBoxStyle,
    zIndex: 1,
    border: updatedStyling
      ? `1px solid ${theme.palette.primary.main}`
      : undefined,
    borderRadius: '4px',
    backgroundColor: theme.palette.primary.main,
    boxShadow: !updatedStyling ? theme.colors.shadows.checkBox : undefined,
    color: theme.background?.white,
    pointerEvents: 'none',
    '& svg': {
      fontSize: 16,
    },
  })
);

const StyledCheckbox = styled(
  ({ updatedStyling, ...props }: ExtendedCheckboxProps) => (
    <CheckBox
      disableRipple
      icon={<UncheckedIcon updatedStyling={updatedStyling} />}
      checkedIcon={
        <CheckedIcon updatedStyling={updatedStyling}>
          <Icon
            name={'Check'}
            customSizes={{ height: 15, width: 15 }}
            iconProps={{ fontSize: 'inherit' }}
          />
        </CheckedIcon>
      }
      {...props}
    />
  )
)<CheckboxProps>(({ theme, size }) => {
  const dimension = size === 'small' ? 35 : 42;

  return {
    width: dimension,
    height: dimension,
    padding: 0,
    borderRadius: '99px',
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover::before': {
      content: '""',
      position: 'absolute',
      width: dimension,
      height: dimension,
      borderRadius: '99px',
      background: theme.background?.chosenItem,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 0,
    },
    '& .MuiTouchRipple-root': {
      display: 'none',
    },
  };
});

const Checkbox = React.forwardRef<HTMLInputElement, ExtendedCheckboxProps>(
  ({ updatedStyling, ...props }, ref) => {
    return (
      <StyledCheckbox
        inputRef={ref}
        {...props}
        updatedStyling={updatedStyling}
      />
    );
  }
);

Checkbox.displayName = 'CustomCheckbox';

export default Checkbox;
export type { CheckboxProps };
export { Checkbox };
